'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { teamMember, WORKFLOW_STATUS } from '../../demo-data'
import { useVisualCopy, type VisualCopy, type VisualFormat } from '../../visual-copy'
import styles from '../mobile-visual.module.css'

/** A cell on the phone's month grid: what it says and which colour says it. */
export type Tone = 'done' | 'busy' | 'recv' | 'open' | 'none'
export type Cell = { label: string; tone: Tone }
export type RowSpec = { name: string; nr: string; tag: string; c1: Cell; c2: Cell }

export function within(beat: number, [from, to]: readonly [number, number]): boolean {
  return beat >= from && beat < to
}

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive screen state. */
/**
 * The step each phone rests on for SSR and reduced motion: the design (Anim Mobiel) rests on 8 —
 * the client detail open with the change applied — so the still frame shows the app in use.
 */
export const REST_BEAT = 8

export function effectiveBeat(beat: number | null): number {
  return beat === null ? REST_BEAT : beat
}

export function Icon({
  children,
  size = 15,
  strokeWidth = 1.7,
}: {
  children: ReactNode
  size?: number
  strokeWidth?: number
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    data-sentry-element="svg" data-sentry-component="Icon" data-sentry-source-file="chrome.tsx">
      {children}
    </svg>
  )
}

export function IconButton({ children, active = true }: { children: ReactNode; active?: boolean }) {
  return (
    <span
      className="flex size-7 items-center justify-center rounded-[9px] shadow-sm"
      style={{ background: 'var(--mv-card)', color: 'var(--mv-text)', opacity: active ? 1 : 0 }}
    data-sentry-component="IconButton" data-sentry-source-file="chrome.tsx">
      {children}
    </span>
  )
}

export function StatusBar({ detailOpen, title, subtitle }: { detailOpen: boolean; title: string; subtitle?: string }) {
  return (
    <div className="relative flex min-h-[32px] shrink-0 items-center justify-center px-3 pt-2 pb-1.5" data-sentry-component="StatusBar" data-sentry-source-file="chrome.tsx">
      <span className={clsx('absolute left-2.5', styles.fadeFast)} style={{ opacity: detailOpen ? 1 : 0 }}>
        <IconButton active={detailOpen} data-sentry-element="IconButton" data-sentry-source-file="chrome.tsx">
          <Icon size={15} strokeWidth={2} data-sentry-element="Icon" data-sentry-source-file="chrome.tsx">
            <path d="M15 19l-7-7 7-7" data-sentry-element="path" data-sentry-source-file="chrome.tsx" />
          </Icon>
        </IconButton>
      </span>

      <div className="flex flex-col items-center">
        <span className="text-[11.5px] font-medium">{title}</span>
        <span
          className={clsx('overflow-hidden text-[10px]', styles.maxH)}
          style={{ color: 'var(--mv-text2)', maxHeight: detailOpen ? 0 : 14 }}
        >
          {subtitle}
        </span>
      </div>

      <div className={clsx('absolute right-2.5 flex gap-1.5', styles.fadeFast)} style={{ opacity: detailOpen ? 0 : 1 }}>
        <IconButton data-sentry-element="IconButton" data-sentry-source-file="chrome.tsx">
          <Icon size={15} strokeWidth={1.8} data-sentry-element="Icon" data-sentry-source-file="chrome.tsx">
            <circle cx="11" cy="11" r="7" data-sentry-element="circle" data-sentry-source-file="chrome.tsx" />
            <path d="m21 21-4.3-4.3" data-sentry-element="path" data-sentry-source-file="chrome.tsx" />
          </Icon>
        </IconButton>
        <IconButton data-sentry-element="IconButton" data-sentry-source-file="chrome.tsx">
          <Icon size={15} strokeWidth={1.8} data-sentry-element="Icon" data-sentry-source-file="chrome.tsx">
            <path d="M10.5 6h9.75M3.75 6H7.5m3 12h9.75m-9.75 0H3.75M16.5 12h3.75m-16.5 0H13.5" data-sentry-element="path" data-sentry-source-file="chrome.tsx" />
            <circle cx="9" cy="6" r="1.5" data-sentry-element="circle" data-sentry-source-file="chrome.tsx" />
            <circle cx="15" cy="12" r="1.5" data-sentry-element="circle" data-sentry-source-file="chrome.tsx" />
            <circle cx="9" cy="18" r="1.5" data-sentry-element="circle" data-sentry-source-file="chrome.tsx" />
          </Icon>
        </IconButton>
      </div>
    </div>
  )
}

export function NotificationBanner({ visible, clientName }: { visible: boolean; clientName: string }) {
  const { t } = useVisualCopy()
  return (
    <div
      className={clsx(
        'pointer-events-none absolute top-[26px] right-2 left-2 z-10 flex items-center gap-2 rounded-[12px] px-3 py-2.5',
        styles.slideY,
      )}
      style={{
        background: 'var(--mv-notify-bg)',
        color: '#fff',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-12px)',
        boxShadow: '0 8px 20px var(--mv-notify-shadow)',
      }}
    data-sentry-component="NotificationBanner" data-sentry-source-file="chrome.tsx">
      <span
        className="flex size-[22px] shrink-0 items-center justify-center rounded-[6px] bg-white"
        style={{ color: 'var(--mv-notify-bg)' }}
      >
        <Icon size={13} strokeWidth={3} data-sentry-element="Icon" data-sentry-source-file="chrome.tsx">
          <path d="M5 12l5 5 9-10" data-sentry-element="path" data-sentry-source-file="chrome.tsx" />
        </Icon>
      </span>
      {/* No app-name prefix: inside a phone mock the sender is plain from context, and the prefix
          was what pushed the client's name into an ellipsis — "…afgerond — Noor…". */}
      <span className="min-w-0 truncate text-[10px] leading-tight font-medium">
        <b className="font-bold">{t.screen.jaarplanning.activityDone(t.monthActivity.administratie.label)}</b> —{' '}
        {clientName}
      </span>
    </div>
  )
}

export type TabSpec = { paths: ReactNode }

const TABS: readonly TabSpec[] = [
  {
    paths: (
      <>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
        <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
      </>
    ),
  },
  {
    paths: (
      <>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
        <path d="M3.5 9.5h17" />
        <circle cx="8" cy="13.5" r="1" />
        <circle cx="12" cy="13.5" r="1" />
        <circle cx="16" cy="13.5" r="1" />
        <circle cx="8" cy="17" r="1" />
        <circle cx="12" cy="17" r="1" />
      </>
    ),
  },
  {
    paths: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 2" />
      </>
    ),
  },
  {
    paths: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="3.5" />
        <path d="M8.5 12l2.5 2.5 4.5-5" />
      </>
    ),
  },
  {
    paths: (
      <>
        <rect x="4" y="4" width="6.5" height="6.5" rx="1.6" />
        <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.6" />
        <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.6" />
        <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.6" />
      </>
    ),
  },
]

/** Bottom tab bar. `activeIndex` selects which tab is highlighted: 0 calendar (planning), 2 clock
 *  (uren), 4 grid (klanten) — matching Anim Mobiel's per-`soort` active tab. */
export function TabBar({ activeIndex = 0 }: { activeIndex?: number }) {
  return (
    <div
      className="mx-2.5 mb-2 flex shrink-0 items-center justify-around rounded-full px-2 py-1.5"
      style={{ background: 'var(--mv-card)', boxShadow: '0 2px 10px rgba(20, 23, 28, 0.06)' }}
    data-sentry-component="TabBar" data-sentry-source-file="chrome.tsx">
      {TABS.map((tab, index) => {
        const active = index === activeIndex
        return (
          <span
            key={index}
            className={clsx('flex h-7 min-w-[34px] items-center justify-center rounded-full', styles.tone)}
            style={{
              background: active ? 'var(--mv-scr)' : 'transparent',
              color: active ? 'var(--mv-accent)' : 'var(--mv-text2)',
            }}
          >
            <Icon size={16}>{tab.paths}</Icon>
          </span>
        )
      })}
    </div>
  )
}

/**
 * Mobile visual — a faithful port of the source Claude "Anim Mobiel" design's `soort="planning"`
 * variant: the Offsoo maandplanning screen inside its own phone chrome (Dynamic Island, status
 * bar, home indicator via `PhoneFrame`). Shows the 8-client progress table, then opens
 * Noorderpoort's detail screen where its status advances to "Afgerond" (triggering an in-app
 * notification), before returning to the list — a 13-beat loop driven by `useScenario`.
 * `beat === null` (SSR, no-JS, `prefers-reduced-motion: reduce`) renders the resting beat-0
 * snapshot: the plain client list, nothing mid-flight.
 */

export function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-px" data-sentry-component="Field" data-sentry-source-file="chrome.tsx">
      <span className="text-[7px] font-bold tracking-[0.3px]" style={{ color: 'var(--mv-text2)' }}>
        {label}
      </span>
      <span className="text-[10px] font-semibold tabular-nums" style={{ color: 'var(--mv-text)' }}>
        {value}
      </span>
    </div>
  )
}

export function StatusStepRow({
  step,
  index,
  activeIndex,
  done,
  isLast,
}: {
  step: ReturnType<typeof statusSteps>[number]
  index: number
  activeIndex: number
  done: boolean
  isLast: boolean
}) {
  const active = index === activeIndex
  const ringColor = active ? (done ? 'var(--mv-done-fg)' : 'var(--mv-busy-fg)') : 'var(--mv-seg-grey)'

  return (
    <div
      className="flex items-center gap-2.5 py-2"
      style={{ borderBottom: isLast ? 'none' : `1px solid var(--mv-hairline)` }}
    data-sentry-component="StatusStepRow" data-sentry-source-file="chrome.tsx">
      <span
        className={clsx('flex size-[18px] shrink-0 items-center justify-center rounded-full', styles.tone)}
        style={{ border: `2px solid ${ringColor}`, background: active ? ringColor : 'transparent' }}
      >
        <span
          className={clsx('size-[7px] rounded-full bg-white', styles.fadeFast)}
          style={{ opacity: active ? 1 : 0 }}
        />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <span
          className={clsx('text-[11px]', styles.tone)}
          style={{
            fontWeight: active ? 700 : 500,
            color: active ? (done ? 'var(--mv-done-fg)' : 'var(--mv-text)') : 'var(--mv-text2)',
          }}
        >
          {step.label}
        </span>
        <span
          className={clsx('overflow-hidden text-[7.5px]', styles.maxH)}
          style={{ color: 'var(--mv-text2)', maxHeight: active ? 14 : 0 }}
        >
          {step.sub}
        </span>
      </div>
    </div>
  )
}

export function DetailLabel({ children }: { children: ReactNode }) {
  return (
    <span className="mt-1 pl-0.5 text-[8px] font-bold tracking-[0.05em]" style={{ color: 'var(--mv-text2)' }} data-sentry-component="DetailLabel" data-sentry-source-file="chrome.tsx">
      {children}
    </span>
  )
}

/**
 * Mobile visual — the Offsoo app on a phone, faithful to Anim Mobiel. `variant` picks the screen:
 * `planning` (maandplanning list → work-item detail, homepage + planning), `uren` (week list →
 * registration form, urenregistratie), or `klanten` (client list → client dossier, klantbeheer).
 */

export const statusSteps = (t: VisualCopy, fmt: VisualFormat) =>
  WORKFLOW_STATUS.map((status) => ({
    label: t.workflow[status.key],
    sub: `${teamMember(status.by).name} · ${fmt.shortDate(status.day)} · ${status.at}`,
  }))

// The open steps of the shared administration checklist — the same three the desktop pop-up
// shows, cropped to what a phone screen holds.
